import { useCallback, useEffect, useRef } from 'react';
import { useDebouncedCallback } from 'use-debounce';

interface IUseDebounceNoteUpdateProps {
    note: string;
    callback: (note: string) => void;
}

/**
 * Debounces value change and calls callback after some time.
 *
 * Before calling callback, the returned function must be called,
 * this is to prevent the callback being called while initializing component.
 *
 * @returns activation and cancel functions
 */
export const useDebouncedNoteUpdate = ({
    note,
    callback,
}: IUseDebounceNoteUpdateProps) => {
    const isActive = useRef(false);
    const debounced = useDebouncedCallback(callback, 5000, { maxWait: 10000 });

    // Call the callback on unmount
    useEffect(
        () => () => {
            debounced.flush();
        },
        [debounced]
    );

    // Call callback after debounce, only if already activated
    useEffect(() => {
        if (isActive.current) {
            debounced(note);
        }
    }, [debounced, note]);

    const activate = useCallback(() => {
        isActive.current = true;
    }, []);
    return { activate, cancel: debounced.cancel };
};
